import { useState, useRef, useEffect } from "react";

/**
 * ExpectationPill — a single Ontario expectation code pill.
 *
 * Face shows the code only (e.g. "B2.1"). The full expectation text is
 * revealed on hover (desktop) and tap (mobile). The reveal is an
 * absolutely-positioned popover, so it NEVER shifts layout. Its width is
 * clamped to the viewport so it can't cause horizontal scroll at 390px,
 * and it drops downward so it isn't clipped by the card header.
 */
export default function ExpectationPill({ code, text }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("touchstart", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("touchstart", onDocDown);
    };
  }, [open]);

  return (
    <span
      ref={ref}
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid={`expectation-pill-${code}`}
        aria-expanded={open}
        aria-label={`Ontario expectation ${code}: ${text}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-[#96ded1]/25 px-2.5 py-1 text-[11px] font-extrabold tracking-wide text-[#4a6984] hover:bg-[#96ded1]/40 focus:outline-none focus:ring-2 focus:ring-[#96ded1] transition-colors"
      >
        {code}
      </button>
      {open && (
        <span
          role="tooltip"
          data-testid={`expectation-tip-${code}`}
          className="absolute left-0 top-full z-30 mt-2 block w-[min(78vw,320px)] max-w-[calc(100vw-2rem)] rounded-lg bg-[#1c2d3d] px-3 py-2 text-left text-[12px] font-medium leading-snug text-white shadow-xl"
        >
          <span className="font-extrabold text-[#96ded1]">{code}</span>
          {" — "}
          {text}
        </span>
      )}
    </span>
  );
}
